/* eslint-disable react/jsx-props-no-spreading */
import {
  Ref, useImperativeHandle, useMemo,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Box,
  Autocomplete,
  Typography,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ORGANIZATION_FORM_KEYS, ORGANIZATION_FORM_VALIDATION } from '@/consts/organizationForm';
import {
  buildOrganizationFormSchema,
  IOrganizationFormData,
  OrganizationFormVariant,
} from './schema';
// import LogoUpload from './LogoUpload';
import COUNTRIES_DATA from '@/consts/countriesData';
import { IOrganization } from '@/services/organizations/types';
import { getCurrentOrganizationUuid } from '@/utils/helpers/authHelpers';
import { canPerformAction } from '@/lib/authZ';
import buildOrganizationFormValuesFromOrganization from '@/utils/helpers/organizationHelpers';

export interface OrganizationFormRef {
  submitForm: () => Promise<{
    formData: IOrganizationFormData;
    hasErrors: boolean;
  }>;
}

export interface OrganizationFormProps {
  ref?: Ref<OrganizationFormRef>;
  variant?: OrganizationFormVariant;
  organizationDetails?: IOrganization;
}

const {
  NAME,
  COUNTRY,
  STREET,
  PROVINCE,
  POSTAL,
  OWNER_EMAIL,
  // LOGO,
} = ORGANIZATION_FORM_KEYS;

const OrganizationForm = ({
  ref = undefined,
  variant = 'create',
  organizationDetails = undefined,
}: OrganizationFormProps) => {
  const { t } = useTranslation('organization');

  const validationMessages = useMemo(() => ({
    NAME_REQUIRED: t('ORGANIZATION_FORM_VALIDATION_ERRORS.NAME_REQUIRED'),
    NAME_MIN: t('ORGANIZATION_FORM_VALIDATION_ERRORS.NAME_MIN', {
      min: ORGANIZATION_FORM_VALIDATION.NAME_MIN_LENGTH,
    }),
    NAME_MAX: t('ORGANIZATION_FORM_VALIDATION_ERRORS.NAME_MAX', {
      max: ORGANIZATION_FORM_VALIDATION.NAME_MAX_LENGTH,
    }),
    STREET_MIN: t('ORGANIZATION_FORM_VALIDATION_ERRORS.STREET_MIN', {
      min: ORGANIZATION_FORM_VALIDATION.STREET_MIN_LENGTH,
    }),
    PROVINCE_MIN: t('ORGANIZATION_FORM_VALIDATION_ERRORS.PROVINCE_MIN', {
      min: ORGANIZATION_FORM_VALIDATION.PROVINCE_MIN_LENGTH,
    }),
    POSTAL_MIN: t('ORGANIZATION_FORM_VALIDATION_ERRORS.POSTAL_MIN', {
      min: ORGANIZATION_FORM_VALIDATION.POSTAL_MIN_LENGTH,
    }),
    POSTAL_MAX: t('ORGANIZATION_FORM_VALIDATION_ERRORS.POSTAL_MAX', {
      max: ORGANIZATION_FORM_VALIDATION.POSTAL_MAX_LENGTH,
    }),
    OWNER_EMAIL_REQUIRED: t('ORGANIZATION_FORM_VALIDATION_ERRORS.OWNER_EMAIL_REQUIRED'),
    OWNER_EMAIL_INVALID: t('ORGANIZATION_FORM_VALIDATION_ERRORS.OWNER_EMAIL_INVALID'),
    LOGO_INVALID_TYPE: t('ORGANIZATION_FORM_VALIDATION_ERRORS.LOGO_INVALID_TYPE'),
    LOGO_SIZE_EXCEEDED: t('ORGANIZATION_FORM_VALIDATION_ERRORS.LOGO_SIZE_EXCEEDED', {
      maxSize: ORGANIZATION_FORM_VALIDATION.LOGO_MAX_SIZE_MB,
    }),
  }), [t]);

  const isUpdateVariant = variant === 'update';
  const validationSchema = useMemo(
    () => buildOrganizationFormSchema(validationMessages, {
      ownerEmailRequired: !isUpdateVariant,
    }),
    [validationMessages, isUpdateVariant],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IOrganizationFormData>({
    mode: 'all',
    resolver: zodResolver(validationSchema),
    defaultValues: buildOrganizationFormValuesFromOrganization(
      isUpdateVariant ? organizationDetails : undefined,
    ),
  });

  const organizationFieldsContext = useMemo(() => ({
    organizationUuid: isUpdateVariant && organizationDetails?.orgUuid
      ? organizationDetails.orgUuid
      : getCurrentOrganizationUuid(),
    viewerOrganizationUuid: getCurrentOrganizationUuid(),
  }), [isUpdateVariant, organizationDetails?.orgUuid]);

  const canUpdateOrganizationName = canPerformAction('updateOrganizationProfile', organizationFieldsContext);
  const canUpdateOrganizationBillingAddress = canPerformAction('updateOrganizationBilling', organizationFieldsContext);
  const showOrganizationProfileFields = !isUpdateVariant || canUpdateOrganizationName;
  const showOrganizationBillingFields = !isUpdateVariant || canUpdateOrganizationBillingAddress;

  useImperativeHandle(ref, () => ({
    submitForm: async () => new Promise<{ formData: IOrganizationFormData;
      hasErrors: boolean }>((resolve) => {
      handleSubmit(
        (data) => {
          resolve({ formData: data, hasErrors: false });
        },
        () => {
          resolve({ formData: {} as IOrganizationFormData, hasErrors: true });
        },
      )();
    }),
  }));

  // const logoSection = (
  //   <Box sx={{ mb: 2 }}>
  //     <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
  //       {t('ORGANIZATION_SETTING_CONSTANTS.ORGANIZATION_LOGO')}
  //     </Typography>
  //     <Controller
  //       name={LOGO}
  //       control={control}
  //       render={({ field }) => (
  //         <LogoUpload
  //           value={field.value || null}
  //           onChange={field.onChange}
  //           error={!!errors[LOGO]}
  //           helperText={errors[LOGO]?.message}
  //         />
  //       )}
  //     />
  //   </Box>
  // );

  const detailsSection = (
    <Box sx={{ mb: 2 }}>
      {/* Organization Name */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
          {t('ORGANIZATION_SETTING_CONSTANTS.ORGANIZATION_NAME')}
        </Typography>
        <Controller
          name={NAME}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={t('ORGANIZATION_SETTING_CONSTANTS.ORGANIZATION_NAME_PLACEHOLDER')}
              variant="outlined"
              fullWidth
              error={!!errors[NAME]}
              helperText={errors[NAME]?.message}
            />
          )}
        />
      </Box>

      {/* Organization billing address */}
      {showOrganizationBillingFields && (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
          {t('ORGANIZATION_SETTING_CONSTANTS.ORGANIZATION_BILLING_ADDRESS')}
        </Typography>
        {/* Country */}
        <Box sx={{ mb: 1.5 }}>
          <Controller
            name={COUNTRY}
            control={control}
            render={({ field }) => {
              const countryValue = COUNTRIES_DATA.find((c) => c.label === field.value) || null;
              return (
                <Autocomplete
                  value={countryValue}
                  options={COUNTRIES_DATA}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('ORGANIZATION_SETTING_CONSTANTS.COUNTRY')}
                      placeholder={t('ORGANIZATION_SETTING_CONSTANTS.COUNTRY')}
                      error={!!errors[COUNTRY]}
                      helperText={errors[COUNTRY]?.message}
                    />
                  )}
                  onChange={(_, value) => field.onChange(value?.label || '')}
                />
              );
            }}
          />
        </Box>
        {/* Street */}
        <Box sx={{ mb: 1.5 }}>
          <Controller
            name={STREET}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('ORGANIZATION_SETTING_CONSTANTS.STREET')}
                placeholder={t('ORGANIZATION_SETTING_CONSTANTS.STREET')}
                variant="outlined"
                fullWidth
                error={!!errors[STREET]}
                helperText={errors[STREET]?.message}
              />
            )}
          />
        </Box>

        {/* Province and Postal - Side by side */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              flex: { xs: 'none', sm: '7 0 0%' },
              minWidth: 0,
            }}
          >
            <Controller
              name={PROVINCE}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('ORGANIZATION_SETTING_CONSTANTS.PROVINCE')}
                  placeholder={t('ORGANIZATION_SETTING_CONSTANTS.PROVINCE')}
                  variant="outlined"
                  fullWidth
                  error={!!errors[PROVINCE]}
                  helperText={errors[PROVINCE]?.message}
                />
              )}
            />
          </Box>
          <Box
            sx={{
              flex: { xs: 'none', sm: '3 0 0%' },
              minWidth: 0,
            }}
          >
            <Controller
              name={POSTAL}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('ORGANIZATION_SETTING_CONSTANTS.POSTAL')}
                  placeholder={t('ORGANIZATION_SETTING_CONSTANTS.POSTAL')}
                  variant="outlined"
                  fullWidth
                  error={!!errors[POSTAL]}
                  helperText={errors[POSTAL]?.message}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
      )}

      {/* Owner email */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}>
          {t('ORGANIZATION_SETTING_CONSTANTS.ORGANIZATION_OWNER_EMAIL')}
        </Typography>
        <Controller
          name={OWNER_EMAIL}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={t('ORGANIZATION_SETTING_CONSTANTS.ORGANIZATION_OWNER_EMAIL_PLACEHOLDER')}
              variant="outlined"
              fullWidth
              type="email"
              disabled={isUpdateVariant}
              error={!!errors[OWNER_EMAIL]}
              helperText={errors[OWNER_EMAIL]?.message}
            />
          )}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: isUpdateVariant ? 'column' : 'column-reverse',
          gap: 2,
        }}
      >
        {/* <Box>{logoSection}</Box> */}
        {showOrganizationProfileFields && <Box>{detailsSection}</Box>}
      </Box>
    </Box>
  );
};

export default OrganizationForm;
